import batiquitosImg from "@/assets/lagoons/batiquitos.jpg";
import sanElijoImg from "@/assets/lagoons/san-elijo.jpg";
import sanDieguitoImg from "@/assets/lagoons/san-dieguito.jpg";
import aguaHediondaImg from "@/assets/lagoons/agua-hedionda.jpg";
import buenaVistaImg from "@/assets/lagoons/buena-vista.jpg";
import losPenasquitosImg from "@/assets/lagoons/los-penasquitos.jpg";
import heronImg from "@/assets/lagoons/great-blue-heron.jpg";
import egretImg from "@/assets/lagoons/snowy-egret.jpg";
import ospreyImg from "@/assets/lagoons/osprey.jpg";
import sageImg from "@/assets/lagoons/coastal-sage.jpg";
import pickleweedImg from "@/assets/lagoons/pickleweed.jpg";
import seaDahliaImg from "@/assets/lagoons/sea-dahlia.jpg";

export type LagoonActivity = { label: string; note: string; url?: string };
export type LagoonWildlife = { name: string; image: string; alt: string; note: string };
export type LagoonNearby = {
  label: string;
  href: string;
  kind: "beach" | "brewery" | "museum" | "guide" | "park" | "viewpoint";
  lat?: number;
  lng?: number;
  blurb?: string;
};

export type Lagoon = {
  slug: string;
  name: string;
  city: string;
  tagline: string;
  image: string;
  alt: string;
  blurb: string;
  // Overview card fields (used by /guides/coastal-lagoons)
  activities: string[];
  bestFor: string;
  trailMiles: string;
  dogFriendly: "Yes" | "No" | "Leashed";
  // Detail-page fields
  about: string[];
  quickFacts: {
    trailLength: string;
    difficulty: string;
    dogPolicy: string;
    cost: string;
    bestSeason: string;
    bestTime: string;
  };
  trail: {
    name: string;
    alltrailsUrl: string;
    distanceMi: string;
    elevationFt: string;
    surface: string;
    accessibility: string;
  };
  location: {
    address: string;
    lat: number;
    lng: number;
  };
  parking: {
    lotName: string;
    address: string;
    fee: string;
    hours: string;
    notes: string;
    alternates: string[];
    lat?: number;
    lng?: number;
  };
  mustKnows: string[];
  wildlife: LagoonWildlife[];
  activitiesDetailed: LagoonActivity[];
  nearby: LagoonNearby[];
};

const heron = (note: string): LagoonWildlife => ({
  name: "Great Blue Heron",
  image: heronImg,
  alt: "Great Blue Heron wading in a California coastal salt marsh",
  note,
});
const egret = (note: string): LagoonWildlife => ({
  name: "Snowy Egret",
  image: egretImg,
  alt: "Snowy Egret with yellow feet hunting along a California tidal mudflat",
  note,
});
const osprey = (note: string): LagoonWildlife => ({
  name: "Osprey",
  image: ospreyImg,
  alt: "Osprey perched above a Southern California lagoon",
  note,
});
const sage = (note: string): LagoonWildlife => ({
  name: "Coastal Sage Scrub",
  image: sageImg,
  alt: "Silvery green coastal sage scrub on a Southern California bluff",
  note,
});
const pickleweed = (note: string): LagoonWildlife => ({
  name: "Pickleweed",
  image: pickleweedImg,
  alt: "Segmented green and red pickleweed succulent stems in a salt marsh",
  note,
});
const seaDahlia = (note: string): LagoonWildlife => ({
  name: "Sea Dahlia",
  image: seaDahliaImg,
  alt: "Bright yellow Sea Dahlia wildflower on a Southern California coastal bluff",
  note,
});

export const lagoons: Lagoon[] = [
  {
    slug: "batiquitos",
    name: "Batiquitos Lagoon",
    city: "Carlsbad",
    tagline: "Flat, dog-free trail and one of the county's premier birding spots.",
    image: batiquitosImg,
    alt: "Calm reflective Batiquitos Lagoon at golden hour with walking trail and native sage scrub in Carlsbad, California",
    blurb: "Easy, flat trails and one of the county's premier birding destinations.",
    activities: ["Birding", "Walking", "Photography"],
    bestFor: "Birding · Easy walks",
    trailMiles: "3.3",
    dogFriendly: "No",
    about: [
      "Batiquitos Lagoon sits between Carlsbad and Encinitas, a 610-acre ecological reserve restored in the 1990s after decades of sediment buildup. The flat north-shore trail is the easiest serious bird walk in San Diego County — no elevation, packed dirt, and interpretive panels every quarter mile.",
      "Restored eelgrass beds attract migratory shorebirds in fall and winter; nesting California least terns and western snowy plovers move in each spring. The Batiquitos Lagoon Foundation runs a small Nature Center near the trailhead with volunteer-led walks most Saturday mornings.",
      "Because dogs are not allowed and bikes are limited, the trail stays remarkably quiet — perfect for sunrise photography or a low-key family stroll.",
    ],
    quickFacts: {
      trailLength: "3.3 mi out-and-back",
      difficulty: "Easy",
      dogPolicy: "No dogs",
      cost: "Free",
      bestSeason: "Fall – Spring",
      bestTime: "Sunrise · Golden hour",
    },
    trail: {
      name: "Batiquitos Lagoon Trail",
      alltrailsUrl: "https://www.alltrails.com/trail/us/california/batiquitos-lagoon-trail",
      distanceMi: "3.3",
      elevationFt: "~50",
      surface: "Packed dirt, flat",
      accessibility: "Stroller-friendly first mile; trail narrows past the Nature Center.",
    },
    location: {
      address: "7380 Gabbiano Ln, Carlsbad, CA 92011",
      lat: 33.0892,
      lng: -117.3027,
    },
    parking: {
      lotName: "Gabbiano Lane Trailhead",
      address: "7380 Gabbiano Ln, Carlsbad, CA 92011",
      fee: "Free",
      hours: "Sunrise to sunset",
      notes: "Small lot — fills by 8am on weekends. Roadside parking is allowed.",
      alternates: ["Aviara Pkwy & Batiquitos Dr (overflow)", "Poinsettia Coaster Station (1 mi west)"],
      lat: 33.0901,
      lng: -117.2974,
    },
    mustKnows: [
      "No dogs, no bikes past the Nature Center — strictly enforced to protect nesting birds.",
      "No restrooms at the trailhead. Closest options are at Aviara Park or Poinsettia Park.",
      "Bring binoculars — many bird species are deep in the marsh and hard to spot without optics.",
      "The trail is exposed; wear a hat and sunscreen even on cool mornings.",
      "Nesting season runs Feb–Aug — stay strictly on the trail.",
    ],
    wildlife: [
      heron("Resident along the channel edges; most active at low tide."),
      osprey("Nests on pilings at the east end. Watch for dramatic plunge dives."),
      egret("Common in the shallows near the Nature Center boardwalk."),
      sage("The dry slopes above the trail are textbook coastal sage habitat."),
    ],
    activitiesDetailed: [
      { label: "Birding", note: "200+ recorded species; peak migration October–March." },
      { label: "Walking & jogging", note: "Flat 3.3-mile out-and-back; minimal shade." },
      { label: "Nature Center", note: "Free, open weekends — staffed by Batiquitos Lagoon Foundation volunteers." },
      { label: "Photography", note: "Best light is the first hour after sunrise looking east across the water." },
    ],
    nearby: [
      { label: "Ponto Beach", href: "/beaches", kind: "beach", lat: 33.0795, lng: -117.3098, blurb: "Wide sandy stretch a half-mile west of the trailhead." },
      { label: "Tamarack Beach", href: "/beaches/tamarack", kind: "beach", lat: 33.1542, lng: -117.3528, blurb: "Surf-friendly Carlsbad beach with bathrooms and parking." },
    ],
  },
  {
    slug: "san-elijo",
    name: "San Elijo Lagoon",
    city: "Encinitas",
    tagline: "A 1,000-acre reserve with boardwalks, a free nature center, and Annie's Canyon next door.",
    image: sanElijoImg,
    alt: "Wooden boardwalk winding through San Elijo Lagoon salt marsh under blue sky in Encinitas, California",
    blurb: "Boardwalks, a nature center, and access to the Annie's Canyon slot trail.",
    activities: ["Hiking", "Birding", "Nature Center"],
    bestFor: "Hiking · Education",
    trailMiles: "7.0",
    dogFriendly: "Leashed",
    about: [
      "San Elijo Lagoon — now formally the San Elijo Lagoon Ecological Reserve — is the largest coastal wetland in San Diego County at just over 1,000 acres. The reserve was massively restored between 2017 and 2022, deepening channels and re-grading marsh edges to handle sea-level rise.",
      "The free Nature Collective center off Manchester Avenue is the best place to start — restrooms, water, trail maps, and a small museum. Eight trail segments radiate out, totaling about seven miles when stitched together.",
      "The crown jewel for many visitors is the Annie's Canyon slot trail, a short one-way scramble through wind-carved sandstone walls. Go early — it bottlenecks on weekends.",
    ],
    quickFacts: {
      trailLength: "Up to 7 mi network",
      difficulty: "Easy – Moderate",
      dogPolicy: "Leashed",
      cost: "Free",
      bestSeason: "Year-round",
      bestTime: "Sunrise",
    },
    trail: {
      name: "San Elijo Lagoon Nature Center Loop",
      alltrailsUrl: "https://www.alltrails.com/trail/us/california/san-elijo-lagoon-nature-center-loop",
      distanceMi: "1.8 (loop) · 7.0 (full network)",
      elevationFt: "~100",
      surface: "Boardwalk, decomposed granite",
      accessibility: "Boardwalk loop near the Nature Center is wheelchair-accessible.",
    },
    location: {
      address: "2710 Manchester Ave, Cardiff, CA 92007",
      lat: 33.0156,
      lng: -117.2722,
    },
    parking: {
      lotName: "Nature Collective Visitor Center Lot",
      address: "2710 Manchester Ave, Cardiff, CA 92007",
      fee: "Free",
      hours: "Daily, 9am–5pm (center) · Trails sunrise–sunset",
      notes: "Lot fills by 9am on weekends. Additional small lots at Rios Ave (Solana Beach) and Santa Carina (Annie's Canyon).",
      alternates: ["Rios Ave Trailhead (Solana Beach)", "Solana Hills Dr (Annie's Canyon access)"],
      lat: 33.0163,
      lng: -117.2701,
    },
    mustKnows: [
      "Annie's Canyon is one-way uphill — read the signs or you'll be the awkward person climbing down.",
      "Dogs must be leashed; pick up after them — waste accelerates algae blooms.",
      "Bikes only on designated trails (east basin). Check the trail map at the center.",
      "Mosquitoes can be active at dusk in summer; bring repellent.",
      "Tides influence the west basin — low tide reveals the most birds on exposed mudflats.",
    ],
    wildlife: [
      heron("Year-round resident; the boardwalk is one of the best places to see one up close."),
      egret("Common along the central channel."),
      osprey("Hunts the deeper east basin; look for nests on tall snags."),
      pickleweed("Carpets the marsh edge — it stores salt in its segments."),
    ],
    activitiesDetailed: [
      { label: "Hiking", note: "Seven miles of interconnected trails; Annie's Canyon spur is a must-do." },
      { label: "Birding", note: "300+ species recorded — among the highest in San Diego County." },
      { label: "Nature Center", note: "Free, family-friendly exhibits and weekend programs.", url: "https://thenaturecollective.org/" },
      { label: "Photography", note: "Boardwalk light at sunrise is exceptional." },
    ],
    nearby: [
      { label: "Cardiff Reef", href: "/beaches/cardiff-reef", kind: "beach", lat: 33.0184, lng: -117.2825, blurb: "Classic longboard reef break right across Highway 101." },
      { label: "Swami's", href: "/beaches/swamis", kind: "beach", lat: 33.0344, lng: -117.2937, blurb: "Iconic Encinitas point break and bluff-top park." },
    ],
  },
  {
    slug: "san-dieguito",
    name: "San Dieguito Lagoon",
    city: "Del Mar",
    tagline: "The River Path connects Del Mar's beach to wide-open wetlands.",
    image: sanDieguitoImg,
    alt: "Wading white shorebirds along the River Path Del Mar with golden grasses lining San Dieguito Lagoon at sunset",
    blurb: "The River Path connects Del Mar's beach to wide-open wetlands and migratory bird habitat.",
    activities: ["Walking", "Birding", "Running"],
    bestFor: "Family walks · Migratory birds",
    trailMiles: "2.2",
    dogFriendly: "Leashed",
    about: [
      "The San Dieguito Lagoon stretches inland from Dog Beach, framed by the Del Mar Racetrack to the north and the bluff homes of Solana Beach to the south. A 1990s mitigation project (tied to the relicensing of the San Onofre nuclear plant) restored hundreds of acres of tidal wetland here.",
      "The River Path Del Mar is the most popular access — a flat, wide, decomposed-granite path that runs from Jimmy Durante Blvd eastward to El Camino Real. It's a favorite of runners, families with strollers, and birders alike.",
      "Migration peaks October through March when sandpipers, willets, marbled godwits, and the occasional American white pelican drop in.",
    ],
    quickFacts: {
      trailLength: "2.2 mi out-and-back",
      difficulty: "Easy",
      dogPolicy: "Leashed",
      cost: "Free",
      bestSeason: "Fall – Spring (migration)",
      bestTime: "Morning",
    },
    trail: {
      name: "Coast to Crest Trail at San Dieguito Lagoon",
      alltrailsUrl: "https://www.alltrails.com/trail/us/california/coast-to-crest-trail-at-the-san-dieguito-lagoon",
      distanceMi: "2.2",
      elevationFt: "~20",
      surface: "Decomposed granite, flat",
      accessibility: "Fully stroller and wheelchair accessible.",
    },
    location: {
      address: "2200 Jimmy Durante Blvd, Del Mar, CA 92014",
      lat: 32.9745,
      lng: -117.2540,
    },
    parking: {
      lotName: "San Dieguito River Park — Jimmy Durante Lot",
      address: "2200 Jimmy Durante Blvd, Del Mar, CA 92014",
      fee: "Free",
      hours: "Sunrise to sunset",
      notes: "Lot is closed during major racetrack events — verify the Del Mar event calendar before visiting in summer.",
      alternates: ["Grand Avenue Bridge (San Andres Dr)", "Dog Beach lot (paid, limited)"],
      lat: 32.9772,
      lng: -117.2598,
    },
    mustKnows: [
      "Avoid the path on Del Mar race days — traffic and parking are a mess.",
      "Leashed dogs only. The path connects to Dog Beach a half mile west.",
      "Trail has zero shade — start early in summer.",
      "Cyclists share the path; keep right and pass on the left.",
    ],
    wildlife: [
      egret("Snowy and great egrets stalk the shallows year-round."),
      heron("Look in the deeper channels close to the river mouth."),
      seaDahlia("Blooms March–May on the bluffs flanking the lagoon."),
    ],
    activitiesDetailed: [
      { label: "Walking & running", note: "Flat, wide, predictable — perfect for an easy daily route." },
      { label: "Birding", note: "Peak migration window: October–March." },
      { label: "Cycling", note: "Connects to the broader Coast-to-Crest trail system." },
    ],
    nearby: [
      { label: "Powerhouse Park", href: "/beaches/powerhouse-park", kind: "beach", lat: 32.9595, lng: -117.2666, blurb: "Grassy bluff park overlooking Del Mar's main beach." },
      { label: "Fletcher Cove", href: "/beaches/fletcher-cove", kind: "beach", lat: 32.9908, lng: -117.2706, blurb: "Solana Beach's main beach access just up the coast." },
    ],
  },
  {
    slug: "agua-hedionda",
    name: "Agua Hedionda Lagoon",
    city: "Carlsbad",
    tagline: "San Diego's only lagoon you can paddle — kayaks, SUPs, and waterski rentals on site.",
    image: aguaHediondaImg,
    alt: "Two kayakers paddling on the turquoise waters of Agua Hedionda Lagoon with palm trees on the far shore in Carlsbad",
    blurb: "San Diego's only lagoon that allows boating — kayak, SUP, and waterski rentals on site.",
    activities: ["Kayaking", "SUP", "Discovery Center"],
    bestFor: "Watersports · Families",
    trailMiles: "1.5",
    dogFriendly: "Leashed",
    about: [
      "Agua Hedionda — Spanish for 'stinky water,' a name that long predates today's cleaned-up waters — is split into three basins by Interstate 5 and the railroad. The inner lagoon is the only one in San Diego County open to motorized boating, paddling, and water-skiing.",
      "California Watersports operates kayak, paddleboard, electric boat, and pedal-boat rentals on the south shore. The free Agua Hedionda Discovery Center sits on the bluff above with live touch tanks, native gardens, and family programs — a great rainy-day stop.",
      "A short hiking loop (Hubbs Trail) leaves from the Discovery Center for a quiet half-hour walk above the lagoon.",
    ],
    quickFacts: {
      trailLength: "1.5 mi loop",
      difficulty: "Easy",
      dogPolicy: "Leashed",
      cost: "Free (rentals extra)",
      bestSeason: "April – October",
      bestTime: "Morning (calmer water)",
    },
    trail: {
      name: "Aqua Hedionda Lagoon Northside (Hubbs) Trail",
      alltrailsUrl: "https://www.alltrails.com/trail/us/california/aqua-hedionda-lagoon-northside-trail",
      distanceMi: "1.5",
      elevationFt: "~100",
      surface: "Packed dirt, mild incline",
      accessibility: "Stroller-difficult — sandy sections and short steep pitches.",
    },
    location: {
      address: "1580 Cannon Rd, Carlsbad, CA 92008",
      lat: 33.1416,
      lng: -117.3320,
    },
    parking: {
      lotName: "Agua Hedionda Discovery Center",
      address: "1580 Cannon Rd, Carlsbad, CA 92008",
      fee: "Free",
      hours: "Daily 9am–5pm",
      notes: "Separate paid lot at California Watersports for rentals. Discovery Center lot is for trail and visitor use only.",
      alternates: ["California Watersports lot (paid)", "Bayshore Dr roadside (limited)"],
      lat: 33.1414,
      lng: -117.3309,
    },
    mustKnows: [
      "Reserve rentals ahead in summer — walk-ups frequently turn away.",
      "Wind picks up after 11am; pros and kids paddle early.",
      "The lagoon connects to the desalination plant — swim only in designated zones.",
      "Bring water; the Discovery Center has fountains but no café.",
    ],
    wildlife: [
      osprey("Several active nests on lagoon pilings — easily spotted from the water."),
      heron("Patrols the quieter east basin away from the rental area."),
      egret("Snowy egrets are common along the trail edges."),
    ],
    activitiesDetailed: [
      { label: "Kayak rentals", note: "Singles, tandems, electric boats, pedal boats — by the hour.", url: "https://californiawatersports.com/" },
      { label: "Stand-up paddleboarding", note: "Calm water makes this a great beginner SUP spot." },
      { label: "Discovery Center", note: "Free admission, kids love the touch tanks.", url: "https://aguahedionda.org/" },
      { label: "Hiking", note: "Hubbs Trail loops above the lagoon in about 40 minutes." },
    ],
    nearby: [
      { label: "Tamarack Beach", href: "/beaches/tamarack", kind: "beach", lat: 33.1542, lng: -117.3528, blurb: "Closest swimming beach, a mile west of the lagoon mouth." },
      { label: "Pizza Port Solana Beach", href: "/culture/breweries/pizza-port-solana-beach", kind: "brewery", lat: 32.9912, lng: -117.2581, blurb: "Beloved local brewpub — well-earned post-paddle stop." },
    ],
  },
  {
    slug: "buena-vista",
    name: "Buena Vista Lagoon",
    city: "Oceanside / Carlsbad",
    tagline: "California's only freshwater coastal lagoon — a quiet sanctuary right by the sea.",
    image: buenaVistaImg,
    alt: "Great white egret standing among lush green cattails on the freshwater Buena Vista Lagoon between Oceanside and Carlsbad",
    blurb: "California's only freshwater coastal lagoon — a quiet bird sanctuary right by the sea.",
    activities: ["Birding", "Nature Center"],
    bestFor: "Birding · Quiet visits",
    trailMiles: "0.6",
    dogFriendly: "No",
    about: [
      "Buena Vista is the smallest of the six lagoons and the only one along the entire California coast that remains predominantly freshwater — a quirk caused by a weir installed in 1940. That freshwater character supports a very different bird community: grebes, coots, and cormorants instead of the saltwater shorebirds you'll see elsewhere.",
      "The Audubon Society operates the Buena Vista Nature Center on the Oceanside side, with a short trail along the lagoon edge and a small interpretive room. Weekend bird walks are free.",
      "A long-debated restoration project is in planning — eventually the lagoon may be returned to saltwater, but for now it remains a peaceful freshwater pocket between the freeway and the sea.",
    ],
    quickFacts: {
      trailLength: "0.6 mi",
      difficulty: "Easy",
      dogPolicy: "No dogs",
      cost: "Free",
      bestSeason: "Fall – Spring",
      bestTime: "Morning",
    },
    trail: {
      name: "Buena Vista Lagoon Ecological Reserve Trail",
      alltrailsUrl: "https://www.alltrails.com/trail/us/california/buena-vista-lagoon-ecological-reserve-trail",
      distanceMi: "0.6",
      elevationFt: "Flat",
      surface: "Decomposed granite",
      accessibility: "Stroller-friendly; mostly flat.",
    },
    location: {
      address: "2202 S Coast Hwy, Oceanside, CA 92054",
      lat: 33.1755,
      lng: -117.3520,
    },
    parking: {
      lotName: "Buena Vista Audubon Nature Center",
      address: "2202 S Coast Hwy, Oceanside, CA 92054",
      fee: "Free (donation suggested)",
      hours: "Center: Tue–Sat 10am–4pm",
      notes: "Small lot — overflow on residential streets. Be respectful of neighbors.",
      alternates: ["Maxton Brown Park (Laguna Dr, Carlsbad side)"],
      lat: 33.1758,
      lng: -117.3508,
    },
    mustKnows: [
      "No dogs in the reserve.",
      "Trail is short — pair with a stop in downtown Oceanside or the pier.",
      "Donations keep the Nature Center running; consider a small gift on entry.",
      "Best birding is from the deck behind the Nature Center.",
    ],
    wildlife: [
      heron("Year-round resident along the cattail edges."),
      egret("Snowy and great egrets are reliably present."),
      pickleweed("Less common here than in the saltwater lagoons — look in the lower marsh."),
    ],
    activitiesDetailed: [
      { label: "Birding", note: "200+ species recorded; freshwater specialists are the draw." },
      { label: "Nature Center", note: "Free admission Tue–Sat.", url: "https://bvaudubon.org/" },
      { label: "Family walks", note: "Short and shaded in spots — easy with small kids." },
    ],
    nearby: [
      { label: "Oceanside Pier", href: "/beaches/oceanside-pier", kind: "beach", lat: 33.1928, lng: -117.3852, blurb: "Historic wooden pier and Oceanside's main beach a mile north." },
      { label: "Belching Beaver Oceanside", href: "/culture/breweries/belching-beaver-oceanside", kind: "brewery", lat: 33.1980, lng: -117.3787, blurb: "Downtown Oceanside taproom — easy walk from the pier." },
    ],
  },
  {
    slug: "los-penasquitos",
    name: "Los Peñasquitos Lagoon",
    city: "Torrey Pines",
    tagline: "A pristine salt marsh framing the south end of Torrey Pines State Beach.",
    image: losPenasquitosImg,
    alt: "Salt marsh of Los Peñasquitos Lagoon bordering Torrey Pines State Beach with sandstone cliffs in the distance at golden hour",
    blurb: "A pristine salt marsh framing the south end of Torrey Pines State Beach.",
    activities: ["Walking", "Birding", "Beach Access"],
    bestFor: "Combined beach + marsh visit",
    trailMiles: "1.8",
    dogFriendly: "No",
    about: [
      "Los Peñasquitos Lagoon is the southernmost of the six and arguably the most photogenic — its salt marsh wraps around the base of the Torrey Pines sandstone cliffs and opens directly onto the beach. It's protected as part of Torrey Pines State Reserve and the adjacent State Beach.",
      "There's no dedicated trailhead inside the marsh itself; you experience it from the edges. The best vantage points are the trail behind Lot 1 of the State Beach, the Guy Fleming Trail above in the Reserve, and a short interpretive loop near the Reserve entrance.",
      "Time your visit with a low tide to see exposed mudflats teeming with fiddler crabs, willets, and the occasional white-tailed kite hunting overhead.",
    ],
    quickFacts: {
      trailLength: "1.8 mi (Guy Fleming Trail option)",
      difficulty: "Easy",
      dogPolicy: "No dogs",
      cost: "Reserve parking fee applies",
      bestSeason: "Year-round",
      bestTime: "Low tide · Late afternoon",
    },
    trail: {
      name: "Flintkote Trail (Torrey Pines / Los Peñasquitos Lagoon)",
      alltrailsUrl: "https://www.alltrails.com/trail/us/california/flintkote-torrey-pines-trail",
      distanceMi: "0.7 (loop)",
      elevationFt: "~100",
      surface: "Packed dirt with stairs",
      accessibility: "Not stroller-friendly. The North Beach trail behind Lot 1 is flat.",
    },
    location: {
      address: "12600 N Torrey Pines Rd, San Diego, CA 92037",
      lat: 32.9268,
      lng: -117.2580,
    },
    parking: {
      lotName: "Torrey Pines State Beach — North Lot",
      address: "12600 N Torrey Pines Rd, San Diego, CA 92037",
      fee: "$15–$25 (varies by season)",
      hours: "7am to sunset",
      notes: "Lots fill by 10am on weekends. Free roadside parking along N Torrey Pines Rd (very limited).",
      alternates: ["Carmel Valley Rd roadside (free, walk in)", "Del Mar street parking (~1 mi south)"],
      lat: 32.9293,
      lng: -117.2596,
    },
    mustKnows: [
      "No dogs anywhere in the State Reserve or State Beach.",
      "Pay the entry fee — rangers ticket aggressively.",
      "Stay strictly on the trail; the marsh is fragile and home to endangered species.",
      "Bring layers — fog can roll in even on hot days.",
      "Combine with a walk on Torrey Pines State Beach for the full experience.",
    ],
    wildlife: [
      egret("Snowy egrets work the mudflats at low tide."),
      heron("Often spotted from the cliff-top trails looking down into the marsh."),
      osprey("Hunts along the river mouth where the lagoon meets the sea."),
      sage("Coastal sage scrub blankets the surrounding hillsides."),
    ],
    activitiesDetailed: [
      { label: "Walking", note: "Pair the marsh edge with a stroll on Torrey Pines State Beach." },
      { label: "Birding", note: "Best at low tide when mudflats are exposed." },
      { label: "Photography", note: "Late afternoon light on the sandstone cliffs is the classic shot." },
      { label: "Beach access", note: "Trail behind Lot 1 leads directly to the sand." },
    ],
    nearby: [
      { label: "Torrey Pines Beach", href: "/beaches/torrey-pines", kind: "beach", lat: 32.9213, lng: -117.2580, blurb: "Wide sandy beach below the State Reserve sandstone cliffs." },
      { label: "Blacks Beach", href: "/beaches/blacks-beach", kind: "beach", lat: 32.8826, lng: -117.2531, blurb: "Long stretch below the gliderport, accessed via a steep trail south." },
    ],
  },
];

export const getLagoon = (slug?: string) => lagoons.find((l) => l.slug === slug);
